import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpen, ExternalLink, FlaskConical, ScanText, Sparkles } from 'lucide-react';
import {
  REASON_LABELS,
  SENTENCE_TEXT_MAX,
  type LongSentenceItemView,
  type SentenceAnalysisResult,
} from '@en3/shared';
import { analysisApi } from '@/api/analysis.api';
import { queryKeys } from '@/api/client';
import { contentApi } from '@/api/content.api';
import { toDisplayMessage } from '@/api/errors';
import { ROUTES } from '@/app/routes';
import { AnalysisCard } from '@/components/analysis/AnalysisCard';
import { EmptyBlock, ErrorBlock, LoadingBlock, Pagination } from '@/components/common/States';
import { Badge, Card, CardBody, CardHeader, CardTitle, Select, Textarea } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';

/**
 * 长难句实验室。
 *
 * 这一页解决的是"读到一个很长的句子，看不懂，从哪下手"。结构上分两块：
 *
 * 1. **文章里的长难句清单**（跨文章、可筛选、可分页）。数据来自 `/api/long-sentences`，
 *    它只返回当前用户可见的文章（公共库 + 自己导入的），所以未登录也能用 ——
 *    这是刻意的：长难句解析是最不需要登录的功能，先让它能跑起来。
 * 2. **粘贴一句话来解析**。用户遇到的难句常常来自别处（试卷、新闻、别人的文章），
 *    这些句子没有 `sentenceId`，只能走 `text` 入口。两者调的是同一个接口、
 *    渲染的是同一个 `AnalysisCard`，因此不存在"粘贴的句子解析结果少了几个模块"的问题。
 *
 * 三个交互决定：
 * - **同一时刻只展开一个解析卡片**。解析卡片很长（主干/从句/语法点/翻译/步骤/仿写），
 *   同时展开多个会让"我刚点的是哪一条"彻底丢失，也让页面在手机上滚不完。
 * - **收起就是收起**（不复用上次的结果）。重新展开会再请求一次，但后端按文本哈希缓存，
 *   命中缓存不消耗 AI 额度；换来的是"展开的一定是当前这句话的结果"这种确定性。
 * - **解析按钮的 loading 只作用于那一句**（用 `mutation.variables` 判定），
 *   而不是整页转圈 —— 否则用户会以为整个列表都在重新加载。
 *
 * 关于难度分：`score` 是后端用规则算出来的 0–100，列表按它降序（"最难的在前"）。
 * 它的用途是**排序**，不是精确度量，所以这里只把它显示成一个档位色的数字，
 * 不做"85 分 = 极难"之类的解读。
 */

const PAGE_SIZE = 10;

/**
 * 难度分 → 徽章色调。
 *
 * 分档只是**视觉提示**，不代表"多少分就是什么水平"（见文件头对 score 的说明）：
 * 40 以下当作一般句子、65 以上才用警示色，避免把"稍微长一点"的句子渲染得像故障。
 */
function difficultyTone(score: number): 'muted' | 'default' | 'warning' | 'danger' {
  if (score >= 80) return 'danger';
  if (score >= 65) return 'warning';
  if (score >= 40) return 'default';
  return 'muted';
}

/** 长难句实验室：跨文章列出长难句，逐条按需解析。 */
export function SentenceLabPage() {
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<'score' | 'recent'>('score');
  const [unanalyzedOnly, setUnanalyzedOnly] = useState(false);
  const [articleId, setArticleId] = useState('');
  const [expanded, setExpanded] = useState<{ sentenceId: string; result: SentenceAnalysisResult } | null>(null);

  const params = useMemo(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      sort,
      unanalyzed: unanalyzedOnly ? true : undefined,
      articleId: articleId.length > 0 ? articleId : undefined,
    }),
    [page, sort, unanalyzedOnly, articleId],
  );

  const list = useQuery({
    queryKey: queryKeys.longSentences(params),
    queryFn: () => analysisApi.longSentences(params),
  });

  /** 文章下拉：一次取 50 篇足够覆盖"我想找刚才那篇"的场景，也避免为了一个下拉做二级检索 */
  const articles = useQuery({
    queryKey: queryKeys.articles({ scope: 'all', pageSize: 50 }),
    queryFn: () => contentApi.articles({ scope: 'all', pageSize: 50 }),
  });

  const analyze = useMutation({
    mutationFn: (item: LongSentenceItemView) => analysisApi.analyze({ sentenceId: item.sentenceId }),
    onSuccess: (result, item) => {
      setExpanded({ sentenceId: item.sentenceId, result });
      // 解析成功后这句话的「已解析」徽章、以及"只看未解析"的筛选结果都变了
      void queryClient.invalidateQueries({ queryKey: ['long-sentences'] });
    },
  });

  const items = list.data?.items ?? [];
  const hasFilters = articleId !== '' || unanalyzedOnly || sort !== 'score';

  function changeFilter(apply: () => void) {
    apply();
    // 换筛选条件必须回第 1 页：停在旧页码会看到"筛选后没有结果"的假空态
    setPage(1);
    setExpanded(null);
  }

  return (
    <section className="mx-auto max-w-3xl px-5 py-8">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <FlaskConical aria-hidden className="size-6 text-primary" />
          长难句实验室
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          平台用规则挑出文章里的长难句（长度 / 从句 / 非谓语 / 介词链 / 插入语），
          再交给解析卡讲清楚：主干是什么、修饰成分挂在哪里、怎么一步步读懂。
        </p>
      </header>

      {/* ------------------------------------------------------------------ */}
      {/* 筛选                                                               */}
      {/* ------------------------------------------------------------------ */}
      <Card className="mt-5">
        <CardBody className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field id="sentence-sort" label="排序">
            <Select
              id="sentence-sort"
              value={sort}
              className="w-full"
              onChange={(event) => changeFilter(() => setSort(event.target.value as 'score' | 'recent'))}
            >
              <option value="score">最难的在前</option>
              <option value="recent">最新的在前</option>
            </Select>
          </Field>

          <Field
            id="sentence-article"
            label="只看某篇文章"
            hint={articles.isError ? '文章列表加载失败，仍可浏览全部长难句。' : undefined}
          >
            <Select
              id="sentence-article"
              value={articleId}
              className="w-full"
              disabled={articles.isPending}
              onChange={(event) => changeFilter(() => setArticleId(event.target.value))}
            >
              <option value="">全部文章</option>
              {(articles.data?.items ?? []).map((article) => (
                <option key={article.id} value={article.id}>
                  {article.titleZh ?? article.title}
                </option>
              ))}
            </Select>
          </Field>

          <div className="flex items-center gap-2 sm:col-span-2">
            <input
              id="sentence-unanalyzed"
              type="checkbox"
              checked={unanalyzedOnly}
              className="size-4 accent-primary"
              onChange={(event) => changeFilter(() => setUnanalyzedOnly(event.target.checked))}
            />
            <label htmlFor="sentence-unanalyzed" className="text-sm">
              只看未解析
            </label>
            <span className="text-xs text-muted-foreground">
              （已解析的句子点开即看，不会重复消耗 AI 额度）
            </span>
          </div>
        </CardBody>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* 列表                                                               */}
      {/* ------------------------------------------------------------------ */}
      <div className="mt-5">
        {list.isPending ? (
          <LoadingBlock label="正在挑长难句…" />
        ) : list.isError ? (
          <ErrorBlock message={toDisplayMessage(list.error)} onRetry={() => void list.refetch()} />
        ) : items.length === 0 ? (
          hasFilters ? (
            <EmptyBlock
              title="当前条件下没有长难句"
              description="换个排序或清空筛选看看；也可以直接在页面底部粘贴一句话来解析。"
              action={
                <Button
                  variant="outline"
                  onClick={() =>
                    changeFilter(() => {
                      setSort('score');
                      setUnanalyzedOnly(false);
                      setArticleId('');
                    })
                  }
                >
                  清空筛选
                </Button>
              }
            />
          ) : (
            <EmptyBlock
              title="还没有可分析的长难句"
              description="长难句来自已导入正文的文章。先去教材目录读一篇文章，导入成功后这里会出现它的长难句。"
              action={
                <Link
                  to={ROUTES.textbooks}
                  className="inline-flex h-9 items-center rounded-md border px-3 text-sm hover:bg-accent hover:text-accent-foreground"
                >
                  去教材目录
                </Link>
              }
            />
          )
        ) : (
          <>
            <p className="text-xs text-muted-foreground">
              共 {list.data?.total ?? 0} 句 · 第 {page} / {list.data?.totalPages ?? 1} 页
            </p>

            <ul className="mt-2 space-y-3">
              {items.map((item) => {
                const isOpen = expanded?.sentenceId === item.sentenceId;
                const isPending = analyze.isPending && analyze.variables?.sentenceId === item.sentenceId;
                const failed = analyze.isError && analyze.variables?.sentenceId === item.sentenceId;
                const title = item.articleTitleZh ?? item.articleTitle;

                return (
                  <li key={item.sentenceId}>
                    <Card>
                      <CardBody className="space-y-2.5">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Badge tone={difficultyTone(item.score)} title="规则算出的难度分（0–100），用于排序">
                            难度 {Math.round(item.score)}
                          </Badge>
                          <Badge tone={item.analyzed ? 'success' : 'muted'}>
                            {item.analyzed ? '已解析' : '未解析'}
                          </Badge>
                          <span className="text-xs text-muted-foreground">{item.tokenCount} 词</span>
                          {item.reasons.map((reason) => (
                            <Badge key={reason} tone="warning" title="判定为长难句的原因">
                              {REASON_LABELS[reason]}
                            </Badge>
                          ))}
                        </div>

                        <p className="font-serif text-sm leading-relaxed break-words">{item.text}</p>

                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <Link
                            to={`${ROUTES.reader(item.articleId)}#s-${item.globalIndex}`}
                            className="inline-flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground hover:text-primary"
                            title="回到原文这一句"
                          >
                            <BookOpen aria-hidden className="size-3.5 shrink-0" />
                            <span className="truncate">
                              {title} · 第 {item.globalIndex + 1} 句
                            </span>
                            <ExternalLink aria-hidden className="size-3 shrink-0" />
                          </Link>

                          <Button
                            size="sm"
                            variant={isOpen ? 'outline' : 'primary'}
                            loading={isPending}
                            onClick={() => {
                              if (isOpen) {
                                setExpanded(null);
                                return;
                              }
                              analyze.mutate(item);
                            }}
                          >
                            <Sparkles aria-hidden className="size-3.5" />
                            {isOpen ? '收起解析' : item.analyzed ? '查看解析' : '解析'}
                          </Button>
                        </div>

                        {failed && (
                          <p
                            role="alert"
                            className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive"
                          >
                            解析失败：{toDisplayMessage(analyze.error)}
                          </p>
                        )}

                        {isOpen && expanded && <AnalysisCard result={expanded.result} className="mt-1" />}
                      </CardBody>
                    </Card>
                  </li>
                );
              })}
            </ul>

            <Pagination page={page} totalPages={list.data?.totalPages ?? 0} onChange={setPage} />
          </>
        )}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* 粘贴一句话                                                          */}
      {/* ------------------------------------------------------------------ */}
      <PasteAnalyzeTool />
    </section>
  );
}

/**
 * 「粘贴一句话来解析」小工具。
 *
 * 为什么必须放在这个页面上：用户遇到难句的场景十有八九不在本站（试卷、公众号、别人发来的
 * 一段话）。如果没有这个入口，他只能"先去导入一篇文章"，而导入是为了分析一句话 ——
 * 成本完全不成比例。这里的说明文字就是要把这件事讲明白。
 *
 * 长度上限用 `SENTENCE_TEXT_MAX`（来自 shared 的 zod schema，与后端校验同一个常量），
 * 不在这里另写 600：两边各写一份，早晚会不一致，届时用户会看到"前端说没超、后端说超了"。
 */
function PasteAnalyzeTool() {
  const [text, setText] = useState('');
  const [result, setResult] = useState<SentenceAnalysisResult | null>(null);

  const trimmed = text.trim();
  const tooLong = trimmed.length > SENTENCE_TEXT_MAX;
  const tooShort = trimmed.length > 0 && trimmed.length < 3;

  const analyze = useMutation({
    mutationFn: (input: string) => analysisApi.analyze({ text: input }),
    onSuccess: setResult,
  });

  const disabled = trimmed.length === 0 || tooLong || tooShort || analyze.isPending;

  return (
    <Card className="mt-8">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ScanText aria-hidden className="size-4 text-primary" />
          粘贴一句话来解析
        </CardTitle>
        <span className="text-xs text-muted-foreground">最多 {SENTENCE_TEXT_MAX} 字</span>
      </CardHeader>

      <CardBody className="space-y-4">
        <p className="text-xs text-muted-foreground">
          这个入口是给<strong>别处看到的句子</strong>用的（试卷、新闻、别人发来的段落）。
          贴进来点「解析」即可，不需要先把它导入成文章；解析结果与文章里的长难句完全一样。
          一次贴一句效果最好 —— 整段一起解析，主干和从句会混在一起，反而不好读。
        </p>

        <Field
          id="paste-sentence"
          label="句子原文"
          hint="英文原文，不要带中文翻译（翻译会让结构分析失准）。"
          error={tooLong ? `超过 ${SENTENCE_TEXT_MAX} 字上限，请只贴一句。` : tooShort ? '太短了，至少 3 个字符。' : undefined}
        >
          <Textarea
            id="paste-sentence"
            rows={3}
            value={text}
            aria-invalid={tooLong || tooShort}
            placeholder="例如：What matters is not how long you study, but whether you keep coming back to it."
            onChange={(event) => {
              setText(event.target.value);
              // 改了输入就不再展示上一次的结果，避免"文字变了但卡片没变"的错觉
              setResult(null);
              analyze.reset();
            }}
          />
        </Field>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className={tooLong ? 'text-xs text-destructive tabular-nums' : 'text-xs text-muted-foreground tabular-nums'}>
            {trimmed.length} / {SENTENCE_TEXT_MAX}
          </span>
          <div className="flex gap-2">
            {text.length > 0 && (
              <Button
                variant="ghost"
                onClick={() => {
                  setText('');
                  setResult(null);
                  analyze.reset();
                }}
              >
                清空
              </Button>
            )}
            <Button
              loading={analyze.isPending}
              disabled={disabled}
              onClick={() => analyze.mutate(trimmed)}
            >
              <Sparkles aria-hidden className="size-3.5" />
              解析
            </Button>
          </div>
        </div>

        {analyze.isError && (
          <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            解析失败：{toDisplayMessage(analyze.error)}
          </p>
        )}

        {result && <AnalysisCard result={result} />}
      </CardBody>
    </Card>
  );
}
